import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Languages, MessageCircle, Mail, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../lib/lang";
import { pages } from "../lib/pagesCopy";
import { PHONE, WA_DEFAULT } from "../lib/copy";
import { BUSINESS } from "../config/business";
import { trackWhatsApp, trackCall } from "../lib/analytics";

const useNavItems = () => {
  const { t, lang } = useLang();
  return [["/", pages[lang].home, "home"], ["/programmes", t.nav.programs, "programmes"], ["/about", t.nav.about, "about"], ["/contact", t.nav.contact, "contact"]];
};

const DesktopNav = () => (
  <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
    {useNavItems().map(([to, label, key]) => (
      <NavLink key={key} to={to} end={to === "/"} data-testid={`nav-link-${key}`}
        className={({ isActive }) => `group relative rounded-full px-4 py-2 font-display text-sm font-bold transition-colors hover:text-ink ${isActive ? "text-ink" : "text-night/80"}`}>
        {({ isActive }) => (
          <>
            {label}
            <span className={`absolute inset-x-4 bottom-1 h-0.5 origin-left rounded bg-sun transition-transform duration-300 group-hover:scale-x-100 ${isActive ? "scale-x-100" : "scale-x-0"}`} />
          </>
        )}
      </NavLink>
    ))}
  </nav>
);

/* Phone menu: a full-screen sheet with large page links and quick contact actions. */
const MobileMenu = ({ onClose }) => {
  const { t } = useLang();
  return (
    <div className="fixed inset-x-0 bottom-0 top-[72px] z-50 flex flex-col overflow-y-auto bg-white lg:hidden" data-testid="mobile-menu">
      <nav className="flex-1 px-5 pt-4" aria-label="Mobile">
        {useNavItems().map(([to, label, key], i) => (
          <NavLink key={key} to={to} end={to === "/"} onClick={onClose} data-testid={`mobile-nav-link-${key}`}
            className={({ isActive }) => `flex items-center gap-4 border-b border-ink/10 py-5 font-display text-2xl font-extrabold ${isActive ? "text-ink" : "text-night/80"}`}>
            {({ isActive }) => (
              <>
                <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs ${isActive ? "bg-sun text-night" : "bg-sky text-ink"}`}>0{i + 1}</span>
                <span className="flex-1">{label}</span>
                <ArrowRight className="h-5 w-5 text-ink/60" aria-hidden="true" />
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-3 bg-mist px-5 py-6">
        <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("mobile-menu")} data-testid="mobile-free-counselling-button"
          className="flex items-center justify-center gap-2 rounded-full bg-sun py-4 font-display font-extrabold text-night"><MessageCircle className="h-5 w-5" aria-hidden="true" />{t.nav.cta}</a>
        <div className="grid grid-cols-2 gap-3">
          <a href={`tel:+91${PHONE}`} onClick={() => trackCall("mobile-menu")} data-testid="mobile-menu-call-link" className="flex items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-display text-sm font-extrabold text-white"><Phone className="h-4 w-4" aria-hidden="true" />{PHONE}</a>
          <a href={`mailto:${BUSINESS.email}`} data-testid="mobile-menu-email-link" className="flex items-center justify-center gap-2 rounded-full border-2 border-ink py-3 font-display text-sm font-extrabold text-ink"><Mail className="h-4 w-4" aria-hidden="true" />Email</a>
        </div>
      </div>
    </div>
  );
};

export const Header = () => {
  const { t, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header className={`sticky top-0 bg-white transition-shadow duration-300 ${open ? "z-[80]" : "z-50"} ${scrolled || open ? "shadow-[0_8px_30px_-12px_rgba(18,24,63,0.25)]" : ""}`} data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
        <Link to="/" aria-label="Mirai Educations home" className="shrink-0 rounded-md"><Logo /></Link>
        <DesktopNav />
        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="language-toggle-button" aria-label="Switch language"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-2 font-display text-xs font-bold text-ink transition-colors hover:bg-sky">
            <Languages className="h-4 w-4" aria-hidden="true" />{t.langLabel}
          </button>
          <a href={`tel:+91${PHONE}`} onClick={() => trackCall("header")} data-testid="header-call-link" aria-label={`Call ${PHONE}`}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sky text-ink lg:hidden">
            <Phone className="h-4 w-4" />
          </a>
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("header")} data-testid="header-free-counselling-button"
            className="hidden rounded-full bg-sun px-5 py-2.5 font-display text-sm font-extrabold text-night transition-[background-color,box-shadow] hover:bg-[#ffb63d] hover:shadow-[0_10px_24px_-10px_rgba(245,165,36,0.9)] sm:inline-flex">
            {t.nav.cta}
          </a>
          <button onClick={() => setOpen((o) => !o)} className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink lg:hidden" aria-label="Menu" aria-expanded={open} data-testid="mobile-menu-button">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
};
